import { ChromeAPIContextProvider } from '@/context/ChromeAPIContextProvider';
import { GmailThreadContextProvider } from '@/context/GmailThreadContextProvider';
import { ChromeLogin as Login } from '@/features/Login';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { ErrorBoundary } from '@sentry/react';
import { MaintenanceGate } from '@shared/components';
import { AuthorizeGmailNotice } from '@shared/components/authorizeInstructions/AuthorizeGmailNotice';
import { GlobalErrorBoundary } from '@shared/components/GlobalErrorBoundary';
import { PrivateRoute } from '@shared/components/PrivateRoute/PrivateRoute';
import { AnalyticsContextProvider } from '@shared/contexts/AnalyticsContext';
import { ApolloWrapper } from '@shared/contexts/ApolloWrapper';
import { AuthContextProvider } from '@shared/contexts/AuthContext';
import { BaseUrlContextProvider } from '@shared/contexts/BaseUrlContext';
import { useMe } from '@shared/contexts/hooks/useMe';
import { MeContextProvider } from '@shared/contexts/MeContext';
import { AuthorizedIntegrationProvider } from '@shared/contexts/pluginContexts/AuthorizedIntegrationContext';
import { StandardLogosContextProvider } from '@shared/contexts/pluginContexts/StandardLogosContextProvider';
import { useAuthContext } from '@shared/contexts/useAuthContext';
import { AutomaticNavigator } from '@shared/features/automatic-navigator/AutomaticNavigator';
import QuoteAuditTrail from '@shared/features/quote-trail/QuoteAuditTrail';
import { AuthTokenType, VoomaApiClient } from '@shared/generated/graphql';
import { useInterval } from '@shared/hooks/useInterval';
import { FlowsViewProvider } from '@shared/plugin/contexts/FlowsViewProvider';
import { PlaceSidePanelContextProvider } from '@shared/plugin/contexts/PlaceSidePanelContext';
import { ToastProvider } from '@shared/plugin/contexts/ToastProvider';
import { AccountSettings } from '@shared/plugin/pages/AccountSettings';
import { ChatsPageWithEmailFeed } from '@shared/plugin/pages/Chats';
import { Home } from '@shared/plugin/pages/Home';
import { PluginLandingRedirect } from '@shared/plugin/pages/Landing';
import { OrderSidePanel } from '@shared/plugin/pages/OrderSidePanel';
import { OriginatorOnboarding } from '@shared/plugin/pages/OriginatorOnboarding';
import { Quotes } from '@shared/plugin/pages/Quotes';
import { QuoteSidePanel } from '@shared/plugin/pages/QuoteSidePanel';
import { ShipmentSidePanel } from '@shared/plugin/pages/ShipmentSidePanel/ShipmentSidePanel';
import { getGraphQLWebSocketUri } from '@shared/utils/apollo';
import {
  checkClientVersion,
  DEFAULT_VERSION_CHECK_INTERVAL,
} from '@shared/utils/version';
import { DrawerTabValue } from 'clerk_common/constants/urls/queryParams';
import {
  ACTIVITY_ROUTE,
  CHATS_ROUTE,
  ORDERS_ROUTE,
  QUOTES_ROUTE,
  SHIPMENTS_ROUTE,
} from 'clerk_common/constants/urls/routes';
import { AnimatePresence } from 'framer-motion';
import { useCallback, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Layout } from '../../Layout';
import { GmailChatsPage } from './GmailChatsPage';

const WEB_BASE_URL = import.meta.env.VITE_WEB_BASE_URL;
const LOGIN_URL = `${import.meta.env.VITE_APP_BASE_URL}/login`;
const APOLLO_URI = `${import.meta.env.VITE_API_BASE_URL}/api`;
const APOLLO_WEBSOCKET_URI = getGraphQLWebSocketUri(APOLLO_URI);

type PanelProps = {
  threadId: string;
};
export const Panel = ({ threadId }: PanelProps) => {
  useInterval(() => {
    checkClientVersion();
  }, DEFAULT_VERSION_CHECK_INTERVAL);
  const location = useLocation();

  return (
    <BaseUrlContextProvider url={WEB_BASE_URL}>
      <AnalyticsContextProvider apiClient={VoomaApiClient.CHROME}>
        <ErrorBoundary fallback={GlobalErrorBoundary}>
          <AnimatePresence>
            <ToastProvider>
              <Auth0ContextProvider>
                <ApolloWrapper
                  appName="gmail"
                  httpLinkUri={APOLLO_URI}
                  webSocketLinkUri={APOLLO_WEBSOCKET_URI}
                >
                  <MeContextProvider useMe={useMe} enableFullStory={false}>
                    <MaintenanceGate isPlugin>
                      <AuthorizedIntegrationProvider
                        type={AuthTokenType.GMAIL_API}
                        renderAuthInstructions={() => <AuthorizeGmailNotice />}
                      >
                        <ChromeAPIContextProvider>
                          <StandardLogosContextProvider>
                            <GmailThreadContextProvider threadId={threadId}>
                              <PlaceSidePanelContextProvider>
                                <FlowsViewProvider>
                                  <AutomaticNavigator>
                                    <Routes
                                      location={location}
                                      key={location.pathname}
                                    >
                                      <Route
                                        path="/login-prompt"
                                        element={<Login />}
                                      />
                                      {/* Private routes */}
                                      <Route
                                        path="/"
                                        element={
                                          <PrivateRoute redirectTo="/login-prompt">
                                            <PluginLandingRedirect />
                                          </PrivateRoute>
                                        }
                                      />
                                      <Route
                                        path={ACTIVITY_ROUTE}
                                        element={
                                          <PrivateRoute redirectTo="/login-prompt">
                                            <Layout
                                              webBaseUrl={WEB_BASE_URL}
                                              hasMenu
                                            >
                                              <Home />
                                            </Layout>
                                          </PrivateRoute>
                                        }
                                      />
                                      <Route
                                        path={CHATS_ROUTE}
                                        element={
                                          <PrivateRoute redirectTo="/login-prompt">
                                            <Layout
                                              webBaseUrl={WEB_BASE_URL}
                                              hasMenu
                                            >
                                              <ChatsPageWithEmailFeed>
                                                <GmailChatsPage />
                                              </ChatsPageWithEmailFeed>
                                            </Layout>
                                          </PrivateRoute>
                                        }
                                      />
                                      <Route
                                        path="/settings"
                                        element={
                                          <PrivateRoute redirectTo="/login-prompt">
                                            <Layout webBaseUrl={WEB_BASE_URL}>
                                              <AccountSettings />
                                            </Layout>
                                          </PrivateRoute>
                                        }
                                      />
                                      <Route
                                        path={QUOTES_ROUTE}
                                        element={
                                          <PrivateRoute redirectTo="/login-prompt">
                                            <Layout webBaseUrl={WEB_BASE_URL}>
                                              <Quotes
                                                webBaseUrl={WEB_BASE_URL}
                                              />
                                            </Layout>
                                          </PrivateRoute>
                                        }
                                      />
                                      <Route
                                        path={`${QUOTES_ROUTE}/:quoteId`}
                                        element={
                                          <PrivateRoute redirectTo="/login-prompt">
                                            <QuoteSidePanel
                                              webBaseUrl={WEB_BASE_URL}
                                              backRoute={ACTIVITY_ROUTE}
                                            />
                                          </PrivateRoute>
                                        }
                                      />
                                      <Route
                                        path={`${QUOTES_ROUTE}/:quoteId/${DrawerTabValue.AUDIT_TRAIL}`}
                                        element={
                                          <PrivateRoute redirectTo="/login-prompt">
                                            <Layout webBaseUrl={WEB_BASE_URL}>
                                              <QuoteAuditTrail />
                                            </Layout>
                                          </PrivateRoute>
                                        }
                                      />
                                      <Route
                                        path={`${QUOTES_ROUTE}/:quoteId/originator-onboarding/:originatorId`}
                                        element={
                                          <PrivateRoute redirectTo="/login-prompt">
                                            <Layout webBaseUrl={WEB_BASE_URL}>
                                              <OriginatorOnboarding />
                                            </Layout>
                                          </PrivateRoute>
                                        }
                                      />
                                      <Route
                                        path={`${ORDERS_ROUTE}/:orderId/originator-onboarding/:originatorId`}
                                        element={
                                          <PrivateRoute redirectTo="/login-prompt">
                                            <Layout webBaseUrl={WEB_BASE_URL}>
                                              <OriginatorOnboarding />
                                            </Layout>
                                          </PrivateRoute>
                                        }
                                      />
                                      <Route
                                        path={`${ORDERS_ROUTE}/:orderId`}
                                        element={
                                          <PrivateRoute redirectTo="/login-prompt">
                                            <OrderSidePanel
                                              webBaseUrl={WEB_BASE_URL}
                                            />
                                          </PrivateRoute>
                                        }
                                      />
                                      <Route
                                        path={`${SHIPMENTS_ROUTE}/:shipmentId`}
                                        element={
                                          <PrivateRoute redirectTo="/login-prompt">
                                            <ShipmentSidePanel
                                              webBaseUrl={WEB_BASE_URL}
                                            />
                                          </PrivateRoute>
                                        }
                                      />
                                      <Route
                                        path="*"
                                        element={
                                          <GlobalErrorBoundary
                                            error={
                                              new Error(
                                                'Issue navigating to page'
                                              )
                                            }
                                          />
                                        }
                                      />
                                    </Routes>
                                  </AutomaticNavigator>
                                </FlowsViewProvider>
                              </PlaceSidePanelContextProvider>
                            </GmailThreadContextProvider>
                          </StandardLogosContextProvider>
                        </ChromeAPIContextProvider>
                      </AuthorizedIntegrationProvider>
                    </MaintenanceGate>
                  </MeContextProvider>
                </ApolloWrapper>
              </Auth0ContextProvider>
            </ToastProvider>
          </AnimatePresence>
        </ErrorBoundary>
      </AnalyticsContextProvider>
    </BaseUrlContextProvider>
  );
};

type Auth0ContextProviderProps = {
  children: JSX.Element;
};
function Auth0ContextProvider({ children }: Auth0ContextProviderProps) {
  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      }}
    >
      <AuthProviderAdapter>{children}</AuthProviderAdapter>
    </Auth0Provider>
  );
}

function AuthProviderAdapter({ children }: { children: JSX.Element }) {
  const { logout } = useAuth0();

  const authLogout = async () => {
    await logout({
      logoutParams: {
        returnTo: `${import.meta.env.VITE_APP_BASE_URL}`,
      },
    });
  };

  return (
    <AuthContextProvider loginUrl={LOGIN_URL} authLogout={authLogout}>
      <AuthConnector>{children}</AuthConnector>
    </AuthContextProvider>
  );
}

function AuthConnector({ children }: { children: JSX.Element }) {
  const { setToken } = useAuthContext();
  const navigate = useNavigate();

  const handleToken = (token: string) => {
    console.log('Received auth token from parent window');
    setToken(token);
    navigate('/');
  };

  const handleTokenCb = useCallback(handleToken, [setToken, navigate]);

  useEffect(() => {
    const handleMessage = (event: {
      origin: string;
      data: { op: string; data: string };
    }) => {
      if (event.origin !== 'https://mail.google.com') {
        return;
      }

      const messageData = event.data;

      switch (messageData.op) {
        case 'SET_AUTH_TOKEN':
          handleTokenCb(messageData.data);
          break;
        default:
          console.warn('[vooma] ignoring unknown message op', messageData.op);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleTokenCb]);

  return children;
}
